import { useState, useEffect } from 'react';
import { Store, Coins, ShoppingCart, Users } from 'lucide-react';
import { API_URL } from '../../config/api';

const StoreComparison = ({ getDateRange, formatCurrency }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch_ = async () => {
      setLoading(true);
      try {
        const dr = getDateRange();
        const params = `start_date=${dr.start}&end_date=${dr.end}`;
        const res = await fetch(`${API_URL}/reports/store-comparison?${params}`);
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch_();
  }, [getDateRange]);

  if (loading) return <div className="empty-state"><div className="empty-icon">⏳</div><p>Mağaza verileri karşılaştırılıyor...</p></div>;
  if (!data.length) return <div className="empty-state"><div className="empty-icon">🏪</div><p>Karşılaştırma verisi bulunamadı</p></div>;

  const totalRevenue = data.reduce((s, d) => s + parseFloat(d.total_revenue || 0), 0);
  const totalSales = data.reduce((s, d) => s + (d.sale_count || 0), 0);
  const colors = ['#0f766e', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6'];

  // KPI metrikler
  const metrics = [
    { key: 'total_revenue', label: 'Toplam Gelir (Tamirat Dahil)', format: v => formatCurrency(v), icon: <Coins size={16} /> },
    { key: 'sale_count', label: 'Satış Adedi', format: v => v, icon: <ShoppingCart size={16} /> },
    { key: 'avg_order', label: 'Ort. Sepet', format: v => formatCurrency(v), icon: <Coins size={16} /> },
    { key: 'unique_customers', label: 'Tekil Müşteri', format: v => v, icon: <Users size={16} /> },
    { key: 'total_items', label: 'Satılan Ürün', format: v => v, icon: <ShoppingCart size={16} /> },
    { key: 'active_days', label: 'Aktif Gün', format: v => v, icon: <Store size={16} /> },
    { key: 'total_expense', label: 'Toplam Gider', format: v => formatCurrency(v), icon: <Coins size={16} /> },
  ];

  return (
    <div className="tab-content">
      {/* Gelir payı özeti */}
      <div className="chart-card">
        <h3>Gelir Payı Dağılımı (Tamirat Dahil)</h3>
        <div style={{ display: 'flex', height: 32, borderRadius: 6, overflow: 'hidden', marginBottom: 12 }}>
          {data.map((store, i) => {
            const pct = totalRevenue > 0 ? (parseFloat(store.total_revenue) / totalRevenue) * 100 : 0;
            return (
              <div key={store.store_id} style={{
                width: `${pct}%`, background: colors[i % colors.length],
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 11, fontWeight: 600, minWidth: pct > 5 ? 'auto' : 0,
                transition: 'width 0.3s'
              }}>
                {pct > 8 ? `${pct.toFixed(1)}%` : ''}
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {data.map((store, i) => (
            <div key={store.store_id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: colors[i % colors.length] }} />
              <span>{store.store_name}</span>
              <span style={{ color: '#5a6c7d' }}>({formatCurrency(store.total_revenue)})</span>
            </div>
          ))}
        </div>
      </div>

      {/* Metrik Karşılaştırma Tablosu */}
      <div className="data-card">
        <h3>Detaylı Karşılaştırma</h3>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Metrik</th>
                {data.map((store, i) => (
                  <th key={store.store_id} style={{ borderBottom: `3px solid ${colors[i % colors.length]}` }}>
                    {store.store_name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {metrics.map(metric => {
                const vals = data.map(s => parseFloat(s[metric.key]) || 0);
                const maxV = Math.max(...vals);
                return (
                  <tr key={metric.key}>
                    <td style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {metric.icon}
                      <strong>{metric.label}</strong>
                    </td>
                    {data.map((store, i) => {
                      const val = parseFloat(store[metric.key]) || 0;
                      const isMax = val === maxV && maxV > 0;
                      return (
                        <td key={store.store_id}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <span style={{ fontWeight: isMax ? 700 : 500, color: isMax ? colors[i % colors.length] : '#2c3e50', fontSize: 14 }}>
                              {metric.format(val)}
                              {isMax && <span style={{ marginLeft: 4, fontSize: 10, color: '#f59e0b' }}>👑</span>}
                            </span>
                            <div style={{ height: 4, background: '#e5e7eb', borderRadius: 2 }}>
                              <div style={{ height: '100%', background: colors[i % colors.length], borderRadius: 2, width: `${maxV > 0 ? (val / maxV) * 100 : 0}%`, opacity: 0.7, transition: 'width 0.3s' }} />
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {/* Net Kar satırı */}
              <tr style={{ borderTop: '2px solid #e5e7eb' }}>
                <td><strong>Net Kar (Gelir - Gider)</strong></td>
                {data.map((store, i) => {
                  const net = parseFloat(store.total_revenue || 0) - parseFloat(store.total_expense || 0);
                  return (
                    <td key={store.store_id}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: net >= 0 ? '#0f766e' : '#e74c3c' }}>
                        {formatCurrency(net)}
                      </span>
                    </td>
                  );
                })}
              </tr>
              {/* Günlük ortalama gelir */}
              <tr>
                <td><strong>Günlük Ort. Gelir</strong></td>
                {data.map((store) => {
                  const dailyAvg = store.active_days > 0 ? parseFloat(store.total_revenue) / store.active_days : 0;
                  return (
                    <td key={store.store_id}>
                      <span style={{ fontWeight: 600 }}>{formatCurrency(dailyAvg)}</span>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StoreComparison;
